/**
 * Utility functions for calculating modified weapon stats based on traits and veterancy
 */

import type { WeaponData } from '../types/weapon';
import type { Unit } from '../types/unit';
import type { Veterancy, TraitModifier } from '../types/veterancy-cohesion';
import { getTraitModifiersMap, VETERANCY_MODIFIERS_MAP } from '../types/veterancy-cohesion';
import { isTraitActive } from '../damage-calculator/trait-activation';
import { Motion } from '../types/motion';

/**
 * Modified weapon stats with base and modified values
 */
export interface ModifiedWeaponStats {
  staticAccuracy: { base: number; modified: number; isModified: boolean };
  movingAccuracy: { base: number | undefined; modified: number | undefined; isModified: boolean };
  aimingTime: { base: number; modified: number; isModified: boolean };
  reloadTime: { base: number; modified: number; isModified: boolean };
  timeBetweenShots?: { base: number; modified: number; isModified: boolean };
  weaponDamage?: { base: number; modified: number; isModified: boolean };
}

/**
 * Calculate modified weapon stats based on trait overrides and veterancy
 * For display purposes, uses dummy context (STATIC motion, 0 distance)
 */
export function calculateModifiedWeaponStats(
  weapon: WeaponData,
  unit: Unit,
  traitOverrides: Map<string, boolean | undefined>,
  veterancy?: Veterancy
): ModifiedWeaponStats {
  const ammo = weapon.ammo;
  
  // Initialize with base values
  const result: ModifiedWeaponStats = {
    staticAccuracy: { 
      base: ammo.staticAccuracy, 
      modified: ammo.staticAccuracy, 
      isModified: false 
    },
    movingAccuracy: { 
      base: ammo.movingAccuracy, 
      modified: ammo.movingAccuracy, 
      isModified: false 
    },
    aimingTime: { 
      base: ammo.aimingTime, 
      modified: ammo.aimingTime, 
      isModified: false 
    },
    reloadTime: { 
      base: ammo.reloadTime, 
      modified: ammo.reloadTime, 
      isModified: false 
    },
  };

  // Add timeBetweenShots if it exists (from timeBetweenSalvos)
  if (ammo.timeBetweenSalvos !== undefined) {
    result.timeBetweenShots = {
      base: ammo.timeBetweenSalvos,
      modified: ammo.timeBetweenSalvos,
      isModified: false,
    };
  }

  // Add weapon damage (HE damage) - base value from ammo
  result.weaponDamage = {
    base: ammo.heDamage,
    modified: ammo.heDamage,
    isModified: false,
  };

  // Get trait modifiers map (may throw if not initialized)
  let traitModifiersMap: { [traitName: string]: TraitModifier };
  try {
    traitModifiersMap = getTraitModifiersMap();
  } catch {
    // Trait modifiers map not initialized yet, return base stats
    return result;
  }

  // Create a dummy context for trait activation checking
  // For display purposes, we use STATIC motion and 0 distance
  // For conditional traits like _choc, we'll respect manual overrides
  const dummyContext = {
    sourceUnit: unit,
    targetUnit: unit, // Use same unit as target for stat calculation
    distance: 0,
    sourceMotion: Motion.STATIC,
    targetMotion: Motion.STATIC,
    sourceTraitOverrides: traitOverrides,
    targetTraitOverrides: undefined,
  };

  // Apply veterancy modifiers first
  if (veterancy) {
    const veterancyModifier = VETERANCY_MODIFIERS_MAP[
      veterancy as keyof typeof VETERANCY_MODIFIERS_MAP
    ];

    if (veterancyModifier) {
      // Apply static accuracy modifier
      const staticAccuracyModifier = veterancyModifier.staticAccuracy || ['+', 0];
      if (staticAccuracyModifier[0] === '+') {
        result.staticAccuracy.modified = result.staticAccuracy.modified + staticAccuracyModifier[1] * 100;
      } else if (staticAccuracyModifier[0] === '-') {
        result.staticAccuracy.modified = result.staticAccuracy.modified - staticAccuracyModifier[1] * 100;
      }
      if (staticAccuracyModifier[1] !== 0) {
        result.staticAccuracy.isModified = true;
      }

      // Apply moving accuracy modifier
      if (ammo.movingAccuracy !== undefined) {
        const motionAccuracyModifier = veterancyModifier.motionAccuracy || ['+', 0];
        if (motionAccuracyModifier[0] === '+') {
          result.movingAccuracy.modified = (result.movingAccuracy.modified || 0) + motionAccuracyModifier[1] * 100;
        } else if (motionAccuracyModifier[0] === '-') {
          result.movingAccuracy.modified = (result.movingAccuracy.modified || 0) - motionAccuracyModifier[1] * 100;
        }
        if (motionAccuracyModifier[1] !== 0) {
          result.movingAccuracy.isModified = true;
        }
      }

      // Apply aim time modifier
      if (veterancyModifier.aimTime) {
        result.aimingTime.modified = result.aimingTime.modified * veterancyModifier.aimTime;
        result.aimingTime.isModified = true;
      }

      // Apply reload time modifier
      if (veterancyModifier.reloadTime) {
        result.reloadTime.modified = result.reloadTime.modified * veterancyModifier.reloadTime;
        result.reloadTime.isModified = true;
      }
    }
  }

  // Apply trait modifiers
  for (const traitName of unit.specialities || []) {
    if (traitName.startsWith('_')) {
      const activationResult = isTraitActive(traitName, dummyContext, true);
      if (activationResult.isActive) {
        const traitModifier = traitModifiersMap[traitName];
        if (!traitModifier) continue;

        // Apply static accuracy modifier
        if (traitModifier.staticAccuracy) {
          const [sign, value] = traitModifier.staticAccuracy;
          if (sign === '+') {
            result.staticAccuracy.modified = result.staticAccuracy.modified + value * 100;
          } else {
            result.staticAccuracy.modified = result.staticAccuracy.modified - value * 100;
          }
          result.staticAccuracy.isModified = true;
        }

        // Apply moving accuracy modifier
        if (traitModifier.motionAccuracy && ammo.movingAccuracy !== undefined) {
          const [sign, value] = traitModifier.motionAccuracy;
          if (sign === '+') {
            result.movingAccuracy.modified = (result.movingAccuracy.modified || 0) + value * 100;
          } else {
            result.movingAccuracy.modified = (result.movingAccuracy.modified || 0) - value * 100;
          }
          result.movingAccuracy.isModified = true;
        }

        // Apply aim time modifier (multiplicative)
        if (traitModifier.aimTime) {
          result.aimingTime.modified = result.aimingTime.modified * traitModifier.aimTime;
          result.aimingTime.isModified = true;
        }

        // Apply reload time modifier (multiplicative)
        if (traitModifier.reloadTime) {
          result.reloadTime.modified = result.reloadTime.modified * traitModifier.reloadTime;
          result.reloadTime.isModified = true;
        }

        // Apply time between shots modifier (percentage, stored as multiplier)
        if (traitModifier.timeBetweenShots && result.timeBetweenShots) {
          // timeBetweenShots is a percentage modifier (e.g., -15% = 0.85 multiplier)
          result.timeBetweenShots.modified = result.timeBetweenShots.modified * traitModifier.timeBetweenShots;
          result.timeBetweenShots.isModified = true;
        }

        // Apply weapon damage modifier (percentage, stored as multiplier)
        if (traitModifier.weaponDamage) {
          result.weaponDamage.modified = result.weaponDamage.modified * traitModifier.weaponDamage;
          result.weaponDamage.isModified = true;
        }
      }
    }
  }

  // Cap accuracy at 100%
  result.staticAccuracy.modified = Math.min(result.staticAccuracy.modified, 100);
  if (result.movingAccuracy.modified !== undefined) {
    result.movingAccuracy.modified = Math.min(result.movingAccuracy.modified, 100);
  }

  return result;
}

